/**
 * Timing & Costs Component
 * Displays average hold times (winners, losers, scratch) and total fees/commissions
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { StatisticsCardHeadline } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { DataTable } from "../../../../components/ui/data-table";

interface TimingCostsTableProps {
  backendStats?: {
    average_hold_time_winning?: number | null;
    average_hold_time_losing?: number | null;
    average_hold_time_scratch?: number | null;
    total_fees?: number | null;
    total_commissions?: number | null;
  } | null;
}

interface TimingRow {
  metric: string;
  value: string;
}

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || isNaN(hours)) return "N/A";
  return `${hours.toFixed(2)} hrs`;
};

const TimingCostsTable: React.FC<TimingCostsTableProps> = ({
  backendStats,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const tableData: TimingRow[] = useMemo(() => [
    {
      metric: "Hold Time (Winners)",
      value: formatHours(backendStats?.average_hold_time_winning),
    },
    {
      metric: "Hold Time (Losers)",
      value: formatHours(backendStats?.average_hold_time_losing),
    },
    {
      metric: "Hold Time (Scratch)",
      value: formatHours(backendStats?.average_hold_time_scratch),
    },
    {
      metric: "Total Fees",
      value: backendStats?.total_fees != null ? formatMoney(backendStats.total_fees) : "N/A",
    },
    {
      metric: "Total Commissions",
      value: backendStats?.total_commissions != null ? formatMoney(backendStats.total_commissions) : "N/A",
    },
  ], [backendStats, formatMoney]);

  const columns: ColumnDef<TimingRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-4">
      <div className="mb-4">
        <StatisticsCardHeadline>Timing & Costs</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={tableData}
        showPagination={false}
        emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
      />
    </div>
  );
};

export default TimingCostsTable;
